import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Grid, IconButton, Tooltip} from "@mui/material";
import {DeleteRounded} from "@mui/icons-material";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import {FormAutocomplete, FormTextField} from "../../../../common/form-fields-wrappers";
import {IBillingOwnerCompany, IBillingOwnerVAT, IGenericOption, IRaFillingForm} from "../../../../models/models";
import {bookingDataValueState, driverResidenceCountriesListState} from "../../../state";
import {fetchProvincesByCountryCode} from "../../../services/http.services";
import GoogleAutocompleteWrapper from "../../../../../../util/component/GoogleAutocompleteWrapper";
import {TGenericValidateType, genericValidate, getDataFromApiGoogle} from "../../../../utils";
import {COMPANY_CODES, ITALY_CODE} from "../../../../config";
import {DEFAULT_BO_FIELDS_STATE_VALUES, PUBLIC_AUTHORITY, RA_FILLING_FORM_INITIAL_VALUE} from "../../config";
import {useDebounce} from "../../../../hooks";
import {IBillingCompanyData} from "./types";

const BillingCompanyData = ({type, editable, curLegalForm, findCustomerByUniqueDataQuery, forceReadOnly}: IBillingCompanyData) => {
	const {t} = useTranslation(['network', 'translation']);
	const raFillingDataForm = useFormContext<IRaFillingForm>();
	const countriesList = useRecoilValue(driverResidenceCountriesListState);
	const [billingPlace, setBillingPlace] = useState<any>(undefined);
	const publicAuthorityList = PUBLIC_AUTHORITY;
	const bookingDataValue = useRecoilValue(bookingDataValueState);

	const [billingCountryDisable, setBillingCountryDisable] = useState<boolean>(true);
	const [billingCityDisable, setBillingCityDisable] = useState<boolean>(true);
	const [billingAddressDisable, setBillingAddressDisable] = useState<boolean>(true);
	const [billingStreetNumberDisable, setBillingStreetNumberDisable] = useState<boolean>(true);
	const [billingPostcodeDisable, setBillingPostcodeDisable] = useState<boolean>(true);
	const [billingProvinceDisable, setBillingProvinceDisable] = useState<boolean>(true);

	const [vatNumberCountryVal, setVatNumberCountryVal] = useState<string>('');
	const [val, setVal] = useState<string>('');
	const [field, setField] = useState<TGenericValidateType | null>(null)
	const [isPa, setIsPa] = useState<boolean>(false);

	const [billingDataCheck, setBillingDataCheck] = useState(DEFAULT_BO_FIELDS_STATE_VALUES.company);
	const [boRr]: any = useWatch({control: raFillingDataForm.control, name: [`billingOwnerInfo.${type}.${curLegalForm}`]});

	const setValueCheck = (field: TGenericValidateType, val: string) => {
		setField(field);
		setVal(val);
	}

	const queryClient = useQueryClient();
	const isVatNumber =  curLegalForm === 'vatNumber';

	const { watch } = useFormContext<IRaFillingForm>();
	const siren = useWatch({name:`billingOwnerInfo.${type}.${curLegalForm}.siren`});
	const siret = useWatch({name:`billingOwnerInfo.${type}.${curLegalForm}.siret`});
	const vatNumberCountry = watch(`billingOwnerInfo.${type}.${curLegalForm}.vatNumberCountry.code`);
    const billingCountry_code = watch(`billingOwnerInfo.${type}.${curLegalForm}.billingCountry.code`);

	console.log('billingOwnerInfo vatNumberCountry.code: ' + vatNumberCountry);
    console.log('billingOwnerInfo billingCountry.code: ' + billingCountry_code);


	const checkSirenVisibility = () =>{
        let visibility = !!vatNumberCountry ? vatNumberCountry === "FR" : billingCountry_code === "FR";
        return visibility;
    }

	const [billingCountry, customerId, linkedToBa, publicAuthority] =
		useWatch<IRaFillingForm, [`billingOwnerInfo.${typeof type}.${typeof curLegalForm}.billingCountry`, `billingOwnerInfo.${typeof type}.${typeof curLegalForm}.customerId`, `billingOwnerInfo.${typeof type}.${typeof curLegalForm}.linkedToBa`, `billingOwnerInfo.${typeof type}.${typeof curLegalForm}.publicAuthority`]>({
			name: [
				`billingOwnerInfo.${type}.${curLegalForm}.billingCountry`,
				`billingOwnerInfo.${type}.${curLegalForm}.customerId`,
				`billingOwnerInfo.${type}.${curLegalForm}.linkedToBa`,
				`billingOwnerInfo.${type}.${curLegalForm}.publicAuthority`,
			]
		});

	const isEditable = editable && !linkedToBa;

	const googleInputPropsBillingField = {
		onPlaceSelected: (place: any) => selectedBillingAddress(place),
		options: {
			fields: ['address_components'],
			types: ['address']
		}
	}

	/** Select billing country and set value form **/
	const selectedBillingAddress = useCallback((result : any) => {
		setBillingPlace(result);
		console.log('billingOwnerInfo --> curLegalForm : ' + curLegalForm);
		console.log('billingOwnerInfo : ' + JSON.stringify(raFillingDataForm.getValues(`billingOwnerInfo.${type}`)));
	}, [])

	const [[_,legalEntities]] = queryClient.getQueriesData<IGenericOption[]>(['ntw_legal_entities']);

	const filteredLegalEntities = useMemo(() => {
		if (isVatNumber) {
			const individualCompany = legalEntities?.find(({code}) => code === COMPANY_CODES.DITTA_INDIVIDUALE);
			raFillingDataForm.setValue(`billingOwnerInfo.${type}.vatNumber.legalEntity`, individualCompany)
			return individualCompany ? [individualCompany] : [];
		}

		return legalEntities?.filter(({code}) => code !== COMPANY_CODES.DITTA_INDIVIDUALE) || [];
	},[isVatNumber, legalEntities]);

	/** dep: billingPlace, set form value field of billing  **/
	useEffect(() => {
		if(billingPlace) {
			const filters = ['country', 'locality', 'route', 'street_number', 'postal_code', 'administrative_area_level_2', 'administrative_area_level_3'];
			const [country, locality, route, street_number, postal_code, administrative_area_level_2, administrative_area_level_3] = getDataFromApiGoogle(billingPlace?.address_components, filters);

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingCity`, locality?.long_name ?? administrative_area_level_3?.long_name ?? '');
			setBillingCityDisable(!!locality?.long_name);

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingAddress`, route?.short_name ?? '');
			setBillingAddressDisable(!!route?.short_name);

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingStreetNumber`, street_number?.short_name ?? '' );
			setBillingStreetNumberDisable(!!street_number?.short_name);
			if(!!route?.short_name) {
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingAddressGoogleSearch`, route?.short_name ?? '' );
			}

			raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingPostalCode`, postal_code?.short_name ?? '' );
			setBillingPostcodeDisable(!!postal_code?.short_name);

			const countriesCode = countriesList.map((elem: any) => elem.code);
			if(countriesCode.includes(country?.short_name)) {
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingCountry`,  {code: country.short_name, description: country.short_name});
				setBillingCountryDisable(true);
				if(country.short_name === ITALY_CODE) {
					raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingProvince`, {code: administrative_area_level_2?.short_name, description: administrative_area_level_2?.short_name});
					setBillingProvinceDisable(!!administrative_area_level_2?.short_name);
				} else {
					raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingProvince`,  RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingProvince ?? null);
					setBillingProvinceDisable(false);
				}
			} else {
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingCountry`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingCountry ?? null);
				setBillingCountryDisable(false);
				raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingProvince`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingProvince ?? null);
				setBillingProvinceDisable(false);
			}
		}
	}, [billingPlace]);

	useDebounce(() => {
		if(!!field && genericValidate(field, val, vatNumberCountryVal, curLegalForm)) {
			const curVal = field === 'vatNumber' ? vatNumberCountryVal + val : val;
			findCustomerByUniqueDataQuery.mutate({[field]: curVal, partnerType: '2'})
		}
	}, 1200, [field, val, vatNumberCountryVal, curLegalForm]);

	/** reset value billing address data **/
	const handleResetBillingAddressFields = useCallback(() => {
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingAddressGoogleSearch`,  '');
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingCountry`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingCountry ?? null);
		setBillingCountryDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingCity`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingCity ?? '');
		setBillingCityDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingAddress`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingAddress ?? '');
		setBillingAddressDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingStreetNumber`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingStreetNumber ?? '');
		setBillingStreetNumberDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingPostalCode`,  RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingPostalCode ?? '');
		setBillingPostcodeDisable(true);
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingProvince`, RA_FILLING_FORM_INITIAL_VALUE!.billingOwnerInfo?.[type]?.[curLegalForm]?.billingProvince ?? null);
		setBillingProvinceDisable(true);
	}, []);

	const handleResidenceCountryChange = () => {
		raFillingDataForm.setValue(`billingOwnerInfo.${type}.${curLegalForm}.billingProvince`, null);
	}

	const provincesQuery = useQuery({
		queryKey: ['ntw_provinces', billingCountry?.code],
		queryFn: () => {
			if (!billingCountry) {return null;}
			return fetchProvincesByCountryCode({code: billingCountry.code});
		},
		enabled: !!billingCountry,
		select: ({response}: any) => response.map((v: any) => ({code: v.codeProvince, description: v.description}))
	});

	const isPristine = (field: keyof (IBillingOwnerCompany | IBillingOwnerVAT)): boolean =>
		!!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.${field}`) && !raFillingDataForm.control.getFieldState(`billingOwnerInfo.${type}.${curLegalForm}.${field}`).isDirty;

	const isFill = (field: keyof (IBillingOwnerCompany | IBillingOwnerVAT)): boolean =>
		!!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.${field}`);

	useEffect(() => {
		setIsPa(publicAuthority?.code === '1');
	}, [publicAuthority]);

	useEffect(() => {

		const billingOwner: any = raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}`);
		const disableAdressFields =
			!!billingOwner.billingCountry &&
			!!billingOwner.billingProvince &&
			!!billingOwner.billingCity &&
			!!billingOwner.billingAddress &&
			!!billingOwner.billingStreetNumber &&
			!!billingOwner.billingPostalCode;

		setBillingDataCheck({
			companyName: !!billingOwner.companyName,
			legalEntity: !!billingOwner.legalEntity,
			publicAuthority: !!billingOwner.publicAuthority,
			vatNumberCountry: !!billingOwner.vatNumberCountry,
			VATNumber: !!billingOwner.VATNumber,
			fiscalCode: !!billingOwner.fiscalCode,
			billingAddressGoogleSearch: disableAdressFields,
			billingCountry: 			disableAdressFields,
			billingProvince: 			disableAdressFields,
			billingCity: 				disableAdressFields,
			billingAddress: 			disableAdressFields,
			billingStreetNumber: 		disableAdressFields,
			billingPostalCode: 			disableAdressFields,
			firstEmail: !!billingOwner.firstEmail,
			certifiedEmail: !!billingOwner.certifiedEmail,
			paIdNumber: !!billingOwner.paIdNumber,
			invoiceCode: !!billingOwner.invoiceCode
		});

		setBillingCountryDisable(disableAdressFields);
		setBillingProvinceDisable(disableAdressFields);
		setBillingPostcodeDisable(disableAdressFields);
		setBillingCityDisable(disableAdressFields);
		setBillingAddressDisable(disableAdressFields);
		setBillingStreetNumberDisable(disableAdressFields);
	}, [boRr])

	return (
		<>
			<Grid container columnSpacing={2}>
				<Grid container item xs={12} columnSpacing={2} >
					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.companyName`}
							label={t("raFilling.billingOwnerInfo.form.label.companyName")}
							control={raFillingDataForm.control}
							fullWidth
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.companyName`)}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.legalEntity`}
							label={t("raFilling.billingOwnerInfo.form.label.legalEntity")}
							control={raFillingDataForm.control}
							options={filteredLegalEntities}
							getOptionLabel={(opt: any) => t(`common.legalForms.${opt.description}`)}
							isOptionEqualToValue={(option: IGenericOption | undefined, value: IGenericOption | undefined) => option?.code === value?.code}
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.legalEntity`)}
						/>
					</Grid>
					{!isVatNumber &&
						<Grid item xs={6} md={3}>
							<FormAutocomplete<IRaFillingForm>
								name={`billingOwnerInfo.${type}.${curLegalForm}.publicAuthority`}
								label={t("raFilling.billingOwnerInfo.form.label.publicAuthority")}
								control={raFillingDataForm.control}
								options={publicAuthorityList}
								getOptionLabel={(opt: any) => t(opt.description)}
								isOptionEqualToValue={(option, value) => option.code === value.code}
								disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.publicAuthority`)}
							/>
					</Grid>
					}
				</Grid>

				<Grid container item xs={12} columnSpacing={2} >
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.vatNumberCountry`}
							label={t("raFilling.billingOwnerInfo.form.label.VATNumberCountry")}
							control={raFillingDataForm.control}
							options={countriesList}
							getOptionLabel={(opt: any) => t(opt.description)}
							isOptionEqualToValue={(option, value) => option.code === value.code}
							onChange={(_e, value) => {setVatNumberCountryVal(value?.code || '')}}
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.vatNumberCountry`)}
						/>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.VATNumber`}
							label={t("raFilling.billingOwnerInfo.form.label.VATNumber")}
							control={raFillingDataForm.control}
							onChange={(e) => {setValueCheck('vatNumber', e.target.value)}}
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.VATNumber`)}
						/>
					</Grid>

					{checkSirenVisibility() &&
                        (<Grid item xs={6} md={3}>
                            <FormTextField<IRaFillingForm>
                                name={`billingOwnerInfo.${type}.${curLegalForm}.siren`}
                                label={t("raFilling.billingOwnerInfo.form.label.siren")}
                                control={raFillingDataForm.control}
                                //onChange={(e) => {setValueCheck('siren', e.target.value)}}	//Filtro search non necessario
                                disabled={forceReadOnly || (!!customerId || !isEditable || bookingDataValue.bookingDetail.flagSubstitution) && billingDataCheck.VATNumber}
                            />
					    </Grid>)
					}

					{checkSirenVisibility() &&
                        (<Grid item xs={6} md={3}>
                            <FormTextField<IRaFillingForm>
                                name={`billingOwnerInfo.${type}.${curLegalForm}.siret`}
                                label={t("raFilling.billingOwnerInfo.form.label.siret")}
                                control={raFillingDataForm.control}
                                //onChange={(e) => {setValueCheck('siret', e.target.value)}}	//Filtro search non necessario
                                disabled={forceReadOnly || (!!customerId || !isEditable || bookingDataValue.bookingDetail.flagSubstitution) && billingDataCheck.VATNumber}
                            />
					    </Grid>)
					}

					<Grid item  xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.fiscalCode`}
							label={t("raFilling.billingOwnerInfo.form.label.fiscalCode")}
							control={raFillingDataForm.control}
							onChange={(e) => {setValueCheck('taxCode', e.target.value)}}
							required={billingCountry?.code === ITALY_CODE}
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.fiscalCode`)}
						/>
					</Grid>

					{checkSirenVisibility() &&
                         (<Grid item xs={6} md={3}>
                              <FormTextField<IRaFillingForm>
                                   name={`billingOwnerInfo.${type}.${curLegalForm}.firstEmail`}
                                   label={t("raFilling.billingOwnerInfo.form.label.email")}
                                   control={raFillingDataForm.control}
                                   fullWidth
                                   onChange={(e) => {setValueCheck('email', e.target.value)}}
                                   disabled={forceReadOnly || (!isEditable || bookingDataValue.bookingDetail.flagSubstitution) && billingDataCheck.firstEmail}
                              />
                         </Grid>)
                    }
				</Grid>

				<Grid item container xs={12} columnSpacing={2} sx={{mb: 'auto'}}>
					<Grid item xs={11} sm={5}>
						<Controller
							name={`billingOwnerInfo.${type}.${curLegalForm}.billingAddressGoogleSearch`}
							control={raFillingDataForm.control}
							render={({field: {onChange, value}}: any) => (
								<GoogleAutocompleteWrapper
									googleInputProps={googleInputPropsBillingField}
									inputProps={{
										fullWidth: true,
										label: t('raFilling.billingOwnerInfo.form.label.googleFilterAddress'),
										id: 'billingAddressGoogleSearch',
										name: `billingOwnerInfo.${type}.${curLegalForm}.billingAddressGoogleSearch`,
										type: 'text',
										value: value || '',
										onChange: (e) => {
											onChange(e.target.value);
											if(!e.target.value) {
												handleResetBillingAddressFields()
											}
										},
										disabled: forceReadOnly || !editable
									}}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={1} alignItems="center" sx={{textAlign: 'center', marginTop: 'auto'}}>
						<Tooltip arrow placement="top" title={t("raFilling.billingOwnerInfo.button.resetBillingAddressValue")}>
							<IconButton disabled={forceReadOnly || !editable}
										onClick={handleResetBillingAddressFields}>
								<DeleteRounded fontSize="small" />
							</IconButton>
						</Tooltip>
					</Grid>
					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.billingCountry`}
							label={t("raFilling.billingOwnerInfo.form.label.billingCountry")}
							control={raFillingDataForm.control}
							options={countriesList}
							getOptionLabel={(opt: any) => t(countriesList.find((x:any)=>x.code === opt.code)?.description)}
							isOptionEqualToValue={(option, value) => option?.code === value?.code}
							onChange={(_e: React.SyntheticEvent) => handleResidenceCountryChange()}
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.billingCountry`)}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormAutocomplete<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.billingProvince`}
							label={t("raFilling.billingOwnerInfo.form.label.billingProvince")}
							control={raFillingDataForm.control}
							options={provincesQuery.data ?? []}
							loading={provincesQuery.isInitialLoading}
							getOptionLabel={(opt) => opt?.description}
							isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
							required={billingCountry?.code === ITALY_CODE}
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.billingProvince`)}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.billingCity`}
							label={t("raFilling.billingOwnerInfo.form.label.billingCity")}
							control={raFillingDataForm.control}
							fullWidth
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.billingCity`)}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.billingAddress`}
							label={t("raFilling.billingOwnerInfo.form.label.billingAddress")}
							control={raFillingDataForm.control}
							fullWidth
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.billingAddress`)}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.billingStreetNumber`}
							label={t("raFilling.billingOwnerInfo.form.label.billingStreetNumber")}
							control={raFillingDataForm.control}
							fullWidth
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.billingStreetNumber`)}
						/>
					</Grid>

					<Grid item xs={6} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.billingPostalCode`}
							label={t("raFilling.billingOwnerInfo.form.label.billingPostalCode")}
							control={raFillingDataForm.control}
							fullWidth
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.billingPostalCode`)}
						/>
					</Grid>
				</Grid>

				{!checkSirenVisibility() &&
                    (<Grid item xs={6} md={3}>
                        <FormTextField<IRaFillingForm>
                            name={`billingOwnerInfo.${type}.${curLegalForm}.firstEmail`}
                            label={t("raFilling.billingOwnerInfo.form.label.email")}
                            control={raFillingDataForm.control}
                            fullWidth
                            onChange={(e) => {setValueCheck('email', e.target.value)}}
						disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.firstEmail`)}
                        />
				    </Grid>)
				}

				{!checkSirenVisibility() &&
                    (<Grid item xs={6} md={3}>
					    <FormTextField<IRaFillingForm>
                            name={`billingOwnerInfo.${type}.${curLegalForm}.certifiedEmail`}
                            label={t("raFilling.billingOwnerInfo.form.label.certifiedEmail")}
                            control={raFillingDataForm.control}
                            fullWidth
						disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.certifiedEmail`)}
					    />
				    </Grid>)
				}
				{ !isVatNumber && isPa &&
					<Grid item xs={12} md={3}>
						<FormTextField<IRaFillingForm>
						name={`billingOwnerInfo.${type}.${curLegalForm}.paIdNumber`}
						label={t("raFilling.billingOwnerInfo.form.label.paIdNumber")}
						control={raFillingDataForm.control}
						fullWidth
						disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.paIdNumber`)}
						/>
					</Grid>
				}
				{ (!isPa || isVatNumber) && !checkSirenVisibility() &&
					(<Grid item xs={12} md={3}>
						<FormTextField<IRaFillingForm>
							name={`billingOwnerInfo.${type}.${curLegalForm}.invoiceCode`}
							label={t("raFilling.billingOwnerInfo.form.label.invoiceCode")}
							control={raFillingDataForm.control}
							fullWidth
							disabled={(forceReadOnly || !editable) && !!raFillingDataForm.getValues(`billingOwnerInfo.${type}.${curLegalForm}.invoiceCode`)}
						/>
					</Grid>)
				}
			</Grid>
		</>
	);
}

export default BillingCompanyData;
